/* eslint-disable no-bitwise */
import { CheckCircle, Email, FileDownloadOutlined } from '@mui/icons-material';
import { alpha, Box, Button, Collapse, Fade, Link, Theme, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import KivraIcon from 'src/assets/images/Kivra-icon.png';
import BrevIcon from 'src/assets/images/Brev-icon.png';
import { rowSx, nodeStyle, lockedSx } from 'src/modules/sending/components/sendingWizardCardStyles';
import { CH_EMAIL, CH_KIVRA, CH_LETTER, SelectionCoverage } from 'src/modules/sending/contexts/sendingWizardContext';
import { bitOfMethod, reachOf, computeSegments } from 'src/modules/sending/functions/deliveryReach';

export type DeliveryMethod = {
  key: string;
  label: string;
  icon: JSX.Element;
  bit: number;
  failText: string;
};

// Nycklarna sparas som en komma-separerad, ordnad prioritetslista i deliveryMethodOrder, t.ex. "mail,kivra,letter"
export const deliveryMethods: DeliveryMethod[] = [
  // Standardblå ikon som korten i steg 1 (primary.main). Kivra/PostNord är loggor i egen färg.
  {
    key: 'mail',
    label: 'E-post',
    icon: <Email color='primary' sx={{ fontSize: '2.4rem' }} />,
    bit: CH_EMAIL,
    failText: 'Om medlemmen saknar e-post',
  },
  {
    key: 'kivra',
    label: 'Kivra',
    icon: <Box component='img' src={KivraIcon} sx={{ maxWidth: '2.4rem' }} alt='Kivra' />,
    bit: CH_KIVRA,
    failText: 'Om medlemmen inte finns i Kivra',
  },
  {
    key: 'letter',
    label: 'Brev',
    icon: <Box component='img' src={BrevIcon} sx={{ maxWidth: '2.4rem' }} alt='PostNord' />,
    bit: CH_LETTER,
    failText: 'Om medlemmen saknar postnummer',
  },
  {
    // PDF-nedladdning: vanlig per-rad-metod utan kanal-bit. Når alla återstående mottagare (du laddar ner dem själv).
    key: 'pdf',
    label: 'PDF-nedladdning',
    icon: <FileDownloadOutlined color='action' sx={{ fontSize: '2.4rem' }} />,
    bit: 0,
    failText: 'Övriga',
  },
];

export const labelOf = (key: string): string => deliveryMethods.find((m) => m.key === key)?.label ?? key;
export const failTextFor = (key: string): string => deliveryMethods.find((m) => m.key === key)?.failText ?? '';
export const KIVRA_HELP_URL = 'https://hjalp.foreningssupport.se/article/370-aktivera-kivra';

// Delad kort-stil för de fyra leveransmetoderna (2×2-rutnät): vitt kort, grön kant + grön ton när valt.
const methodCardSx = {
  width: '100%',
  minHeight: 124,
  py: 2.5,
  px: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  backgroundColor: '#fff',
  position: 'relative',
  color: 'text.primary',
  borderWidth: 2,
  borderRadius: 1.5,
  borderColor: 'divider',
  transition: 'border-color .2s ease, background-color .2s ease',
  '&:hover': { borderWidth: 2, borderColor: 'success.main', backgroundColor: (t: Theme) => alpha(t.palette.success.light, 0.2) },
  '&.selected': { borderWidth: 2, borderColor: 'success.main', backgroundColor: (t: Theme) => alpha(t.palette.success.light, 0.2) },
};

type DeliveryMethodPickerProps = {
  // Komma-separerad prioritetslista, t.ex. "mail,kivra,letter".
  value: string;
  onChange: (next: string) => void;
  // Täckningen styr räckviddssiffrorna på korten. Utan täckning visas korten utan siffror – väljaren fungerar ändå.
  coverage?: SelectionCoverage | null;
  // Täckningen räknas i två steg; medan masks fylls i visar korten "…" i stället för 0.
  channelsLoading?: boolean;
};

/**
 * Prioritetsväljaren för leveransmetoder: tre nivåer på en gemensam tidslinje där varje nivå tänds när den
 * föregående fått ett val. Delas av utskickswizardens Leveransmetoder-steg och beställningen av påminnelser,
 * så de två aldrig glider isär visuellt.
 */
export const DeliveryMethodPicker = ({ value, onChange, coverage = null, channelsLoading = false }: DeliveryMethodPickerProps) => {
  const order = useMemo(() => (value ? value.split(',').filter((k) => k.length > 0) : []), [value]);

  const pre = coverage?.approx ? '≈ ' : '';

  const pickMethod = useCallback(
    (rowIndex: number, key: string) => {
      let next: string[];
      if (order[rowIndex] === key) {
        // Avmarkera → ta bort denna rad och efterföljande (undvik luckor i prioritetsordningen)
        next = order.slice(0, rowIndex);
      } else if (rowIndex < order.length) {
        next = [...order];
        next[rowIndex] = key;
      } else {
        next = [...order, key];
      }
      onChange(next.join(','));
    },
    [order, onChange],
  );

  const segments = computeSegments(order, coverage);
  // Hur många som redan nåtts efter de N första nivåerna (för att dölja onödiga fallback-nivåer när alla redan nåtts).
  const reachedAfter = (n: number): number => segments.slice(0, n).reduce((sum, s) => sum + s.inc, 0);

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          position: 'absolute',
          left: { xs: 20, sm: 24 },
          top: 0,
          bottom: 0,
          borderLeft: '2px dashed',
          borderColor: 'grey.400',
          zIndex: 0,
        }}
      />

      {[0, 1, 2].map((rowIndex) => {
        // Väljer man PDF-nedladdning är det slut där (den når alla återstående) – inga fler nivåer visas.
        const priorPdf = order.slice(0, rowIndex).includes('pdf');
        // ...likaså om alla mottagare redan nåtts av tidigare metoder.
        const allReachedBefore = coverage != null && reachedAfter(rowIndex) >= coverage.total;
        // Rad 0 (första hand) är alltid aktiv. Fallback-nivåerna visas tills en metod valts, PDF valts, eller alla nåtts.
        const enabled = rowIndex === 0 || (!priorPdf && !allReachedBefore && rowIndex <= order.length);
        const locked = !enabled;
        // Kommande fallback-nivåer är helt dolda tills föregående nivå fått ett val – de tänds stegvis.
        if (locked) return null;
        // Noden blir grön när nivån har ett val.
        const filled = rowIndex < order.length;
        const prevKey = order[rowIndex - 1];
        // Första raden har ingen rubrik; fallback-raderna förklarar varför nästa metod behövs.
        const leadIn = (() => {
          if (rowIndex === 0) return '';
          if (prevKey) return `${failTextFor(prevKey)}, skicka via:`;
          return 'Om föregående inte kan leverera, skicka via:';
        })();
        const rowChoice = order[rowIndex];
        // Stabil kort-uppsättning för raden (dölj metoder valda på ANDRA nivåer). Ordningen ändras INTE när man
        // väljer här – korten byter bara synlighet, så inget hoppar om. Övriga tonas ut + fälls ihop på plats.
        const rowMethods = deliveryMethods.filter((m) => m.key === rowChoice || !order.includes(m.key));
        // Kanaler som redan täckts av tidigare valda nivåer – för att räkna hur många NYA en metod når här.
        const existingBits = order.slice(0, rowIndex).reduce((acc, k) => acc | bitOfMethod(k), 0);

        const reachFor = (method: DeliveryMethod): number | null => {
          if (!coverage) return null;
          return method.key === 'pdf'
            ? Math.max(0, coverage.total - reachOf(coverage, existingBits))
            : reachOf(coverage, existingBits | method.bit) - reachOf(coverage, existingBits);
        };

        // Ett metod-kort. Förstahandsvalet visar full räckvidd; fallback-nivåer visar hur många NYA metoden når
        // utöver tidigare val (t.ex. Kivra via personnummer för dem som saknar e-post). PDF når alla återstående.
        const renderMethodCard = (method: DeliveryMethod) => {
          const selected = rowChoice === method.key;
          const kivraNA = method.key === 'kivra' && coverage != null && !coverage.kivraAvailable;
          const reach = reachFor(method);
          return (
            <Button
              key={method.key}
              color='primary'
              variant='outlined'
              className={selected ? 'selected' : ''}
              onClick={() => pickMethod(rowIndex, method.key)}
              sx={methodCardSx}
            >
              {method.icon}
              <Typography variant='body1' component='span' sx={{ fontWeight: 600 }}>
                {method.label}
              </Typography>
              {kivraNA ? (
                <Link
                  href={KIVRA_HELP_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  variant='caption'
                  onClick={(e) => e.stopPropagation()}
                  sx={{ fontWeight: 600 }}
                >
                  Ej aktiverat
                </Link>
              ) : (
                reach != null && (
                  <Typography variant='body2' sx={{ color: reach > 0 ? 'text.secondary' : 'text.disabled' }}>
                    {pre}
                    {channelsLoading ? '…' : reach} {rowIndex === 0 ? 'kan nås' : 'nya'}
                  </Typography>
                )
              )}
              {selected && <CheckCircle sx={{ position: 'absolute', top: 6, right: 6, fontSize: 20, color: (t) => t.palette.success.main }} />}
            </Button>
          );
        };

        // Vald metod visas som en kompakt bred rad (sparar höjd) i stället för det stora kortet. Klick avmarkerar.
        const renderCompactRow = (method: DeliveryMethod) => {
          const kivraNA = method.key === 'kivra' && coverage != null && !coverage.kivraAvailable;
          const reach = reachFor(method);
          return (
            <Box
              onClick={() => pickMethod(rowIndex, method.key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: 1.5,
                border: '2px solid',
                borderColor: 'success.main',
                bgcolor: (t) => alpha(t.palette.success.light, 0.2),
                cursor: 'pointer',
                transition: 'background-color .15s',
                '&:hover': { bgcolor: (t) => alpha(t.palette.success.light, 0.32) },
              }}
            >
              {method.icon}
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                {method.label}
              </Typography>
              {!kivraNA && reach != null && (
                <Typography variant='body2' color='text.secondary'>
                  {pre}
                  {reach} {rowIndex === 0 ? 'kan nås' : 'nya'}
                </Typography>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <CheckCircle sx={{ color: 'success.main' }} />
            </Box>
          );
        };

        return (
          // Nivån tonas mjukt in när den tänds (efter föregående val) så nästa steg märks utan hack.
          <Fade key={rowIndex} in appear timeout={350}>
            <Box sx={{ ...rowSx, ...lockedSx(locked) }}>
              <Box sx={nodeStyle(filled)}>
                {filled ? (
                  <CheckCircle sx={{ fontSize: 22 }} />
                ) : (
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>{rowIndex + 1}</Typography>
                )}
              </Box>

              {leadIn && (
                <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1.5 }}>
                  {leadIn}
                </Typography>
              )}

              <Box>
                {/* Vald metod → kompakt bred rad (tonas in). Utan val → korten att välja bland. */}
                {rowChoice ? (
                  <Fade in appear timeout={300}>
                    <Box>{renderCompactRow(deliveryMethods.find((m) => m.key === rowChoice) as DeliveryMethod)}</Box>
                  </Fade>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    {rowMethods.map((method) => (
                      <Collapse key={method.key} in timeout={300} unmountOnExit>
                        <Box>{renderMethodCard(method)}</Box>
                      </Collapse>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Fade>
        );
      })}
    </Box>
  );
};
